import { useEffect, useState } from "react";
import { api, getAuthHeaders } from "../api";
import type { AiUsageMeter, Case, CaseRecord } from "../types";
import { exportCaseAsPdf } from "../lib/exportPdf";
import { exportCaseAsZip } from "../lib/exportZip";
import {
  clearDemoCase,
  getDemoCaseId,
  seedDemoCase,
} from "../lib/seedDemoCase";
import {
  BRIGHTNESS_MAX,
  BRIGHTNESS_MIN,
  BRIGHTNESS_STEP,
  DEFAULT_BRIGHTNESS,
  getBrightness,
  getTheme,
  setBrightness,
  setTheme,
  subscribeTheme,
  THEME_LABELS,
  THEMES,
  type Theme,
} from "../lib/theme";
import AiSettings from "./AiSettings";
import SubscriptionSettings from "./SubscriptionSettings";
import Tooltip from "./ui/Tooltip";
import ModalBackdrop from "./ui/ModalBackdrop";
import {
  applyAppearance,
  getSettings,
  subscribe,
  updateSettings,
  type FontSize,
  type Retention,
  type Settings,
  type SortOrder,
} from "../lib/settings";

interface Props {
  open: boolean;
  onClose: () => void;
  cases: Case[];
  userEmail: string | null;
  forwardingAddress: string | null;
  onAccountDeleted: () => void;
  // Subscription snapshot — piggybacks on AuthenticatedApp's existing
  // /api/me fetch so Settings doesn't round-trip again on open.
  plan: string;
  daysRemaining: number | null;
  hasSubscription: boolean;
  // Full subscription state used by Plan & Billing to flip between
  // the active / cancelling / renewing branches. Sourced from the
  // same /api/me payload as the rest.
  cancelAtPeriodEnd: boolean;
  currentPeriodEnd: string | null;
  // AI usage snapshot. Rendered inside the Plan & Billing section
  // with a progress bar + reset-date line. Null while the first
  // /api/ai/usage-meter fetch is in flight.
  aiMeter: AiUsageMeter | null;
  // Called after Plan & Billing mutates the subscription (cancel,
  // reactivate, switch plan) so AuthenticatedApp can re-fetch
  // /api/me and the sidebar / trial banner reflect the new state.
  onBillingChanged?: () => void;
  // Optional deep-link: when set, scroll the named section into view
  // on open. Currently the sidebar AI meter uses "subscription" so
  // clicking the meter lands directly on the upgrade controls.
  initialSection?: "subscription" | null;
  // Called after the demo-case seeder finishes so the parent can add
  // the new case to the sidebar list and navigate to it. Mirrors the
  // existing handleCaseCreated path used by NewCaseForm.
  onDemoCaseLoaded?: (created: Case) => void;
  // Called after the demo case is cleared so the parent can drop it
  // from the sidebar list and clear the active view if it was open.
  onDemoCaseCleared?: (id: string) => void;
}

export default function Settings({
  open,
  onClose,
  cases,
  userEmail,
  forwardingAddress,
  onAccountDeleted,
  plan,
  daysRemaining,
  hasSubscription,
  cancelAtPeriodEnd,
  currentPeriodEnd,
  aiMeter,
  onBillingChanged,
  initialSection,
  onDemoCaseLoaded,
  onDemoCaseCleared,
}: Props) {
  const [settings, setSettings] = useState<Settings>(getSettings());
  const [nameDraft, setNameDraft] = useState(settings.account.displayName);
  const [nameSaving, setNameSaving] = useState(false);
  const [nameSaved, setNameSaved] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const [exportBusy, setExportBusy] = useState<
    "csv" | "pdf" | "zip" | null
  >(null);
  const [pdfCaseId, setPdfCaseId] = useState<string>(
    cases[0]?.id ?? "",
  );
  const [zipCaseId, setZipCaseId] = useState<string>(
    cases[0]?.id ?? "",
  );
  const [deleteConfirming, setDeleteConfirming] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [demoBusy, setDemoBusy] = useState<"loading" | "clearing" | null>(
    null,
  );
  const [demoError, setDemoError] = useState<string | null>(null);
  const [demoCaseId, setDemoCaseId] = useState<string | null>(
    getDemoCaseId(),
  );
  const [theme, setThemeState] = useState<Theme>(getTheme());
  const [brightness, setBrightnessState] = useState<number>(getBrightness());

  useEffect(
    () =>
      subscribeTheme(({ theme: t, brightness: b }) => {
        setThemeState(t);
        setBrightnessState(b);
      }),
    [],
  );

  // Keep the export case pickers in sync when the case list loads asynchronously
  // or changes underneath us.
  useEffect(() => {
    if (!pdfCaseId && cases[0]) setPdfCaseId(cases[0].id);
    if (!zipCaseId && cases[0]) setZipCaseId(cases[0].id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cases]);

  useEffect(() => subscribe(setSettings), []);

  useEffect(() => {
    if (!open) return;
    setNameDraft(settings.account.displayName);
    setExportError(null);
    setDeleteConfirming(false);
    setDemoError(null);
    setDemoCaseId(getDemoCaseId());
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    // Deep-link to a section if the opener requested one. Deferred
    // to the next tick so the dialog has finished laying out before
    // scrollIntoView runs.
    if (initialSection) {
      const targetId = `settings-section-${initialSection}`;
      window.setTimeout(() => {
        const el = document.getElementById(targetId);
        el?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 0);
    }

    return () => {
      window.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialSection]);

  if (!open) return null;

  function update<K extends keyof Settings>(
    section: K,
    patch: Partial<Settings[K]>,
  ) {
    updateSettings({
      [section]: { ...settings[section], ...patch },
    } as Partial<Settings>);
  }

  async function saveDisplayName() {
    setNameSaving(true);
    setNameSaved(false);
    try {
      await api.updateProfile({ display_name: nameDraft });
      update("account", { displayName: nameDraft });
      setNameSaved(true);
      window.setTimeout(() => setNameSaved(false), 1500);
    } catch (err) {
      window.alert(`Save failed: ${(err as Error).message}`);
    } finally {
      setNameSaving(false);
    }
  }

  // Server-backed preferences: sync to the backend whenever the user
  // changes them. Failures are non-fatal — the local setting is kept.
  async function updateServerPref(patch: {
    retention?: "never" | "1y" | "3y";
    notify_on_forward?: boolean;
    notify_weekly_summary?: boolean;
  }) {
    try {
      await api.updateProfile(patch);
    } catch (err) {
      console.error("failed to sync preference to server", err);
    }
  }

  async function exportTimelineCsv() {
    setExportError(null);
    if (cases.length === 0) {
      setExportError("No cases to export.");
      return;
    }
    setExportBusy("csv");
    try {
      const allRecords: Array<{ caseName: string; record: CaseRecord }> = [];
      for (const c of cases) {
        const { records } = await api.listRecords(c.id);
        for (const r of records) allRecords.push({ caseName: c.name, record: r });
      }
      const csv = toCsv(allRecords);
      downloadBlob(
        new Blob([csv], { type: "text/csv;charset=utf-8" }),
        `casethread-timeline-${today()}.csv`,
      );
    } catch (err) {
      setExportError((err as Error).message);
    } finally {
      setExportBusy(null);
    }
  }

  async function exportCasePdf() {
    setExportError(null);
    const c = cases.find((x) => x.id === pdfCaseId) ?? cases[0];
    if (!c) {
      setExportError("No case selected.");
      return;
    }
    setExportBusy("pdf");
    try {
      const { records } = await api.listRecords(c.id);
      const blob = await exportCaseAsPdf({ case: c, records });
      downloadBlob(blob, `casethread-${slugify(c.name)}-${today()}.pdf`);
    } catch (err) {
      setExportError((err as Error).message);
    } finally {
      setExportBusy(null);
    }
  }

  async function exportCaseZip() {
    setExportError(null);
    const c = cases.find((x) => x.id === zipCaseId) ?? cases[0];
    if (!c) {
      setExportError("No case selected.");
      return;
    }
    setExportBusy("zip");
    try {
      const { records } = await api.listRecords(c.id);
      const blob = await exportCaseAsZip(
        { case: c, records },
        await getAuthHeaders(),
      );
      downloadBlob(blob, `casethread-${slugify(c.name)}-${today()}.zip`);
    } catch (err) {
      setExportError((err as Error).message);
    } finally {
      setExportBusy(null);
    }
  }

  async function loadDemoCase() {
    setDemoError(null);
    setDemoBusy("loading");
    try {
      const created = await seedDemoCase();
      setDemoCaseId(created.id);
      onDemoCaseLoaded?.(created);
      onClose();
    } catch (err) {
      setDemoError((err as Error).message);
    } finally {
      setDemoBusy(null);
    }
  }

  async function clearDemo() {
    setDemoError(null);
    const id = demoCaseId;
    if (!id) return;
    setDemoBusy("clearing");
    try {
      await clearDemoCase();
      setDemoCaseId(null);
      onDemoCaseCleared?.(id);
    } catch (err) {
      setDemoError((err as Error).message);
    } finally {
      setDemoBusy(null);
    }
  }

  async function deleteAccount() {
    if (!deleteConfirming) {
      setDeleteConfirming(true);
      return;
    }
    setDeleting(true);
    try {
      await api.deleteAccount();
      onAccountDeleted();
    } catch (err) {
      window.alert(`Delete failed: ${(err as Error).message}`);
      setDeleting(false);
    }
  }

  return (
    <ModalBackdrop onClose={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="settings-title"
        className="settings-card"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="settings-header">
          <h2 id="settings-title" className="modal-title">
            Settings
          </h2>
          <Tooltip title="Close settings">
            <button
              type="button"
              className="btn-icon"
              onClick={onClose}
              aria-label="Close settings"
            >
              ✕
            </button>
          </Tooltip>
        </div>

        <div className="settings-body">
          {/* ---------- Appearance ---------- */}
          <section className="settings-section">
            <h3>Appearance</h3>

            <Row
              label="Theme"
              hint="Pick a palette. ‘System’ follows your OS dark/light preference."
            >
              <div className="theme-grid" role="radiogroup" aria-label="Theme">
                {THEMES.map((t) => {
                  const selected = theme === t;
                  return (
                    <button
                      key={t}
                      type="button"
                      role="radio"
                      aria-checked={selected}
                      className={`theme-card${selected ? " selected" : ""}`}
                      onClick={() => setTheme(t)}
                    >
                      <div
                        className={`theme-card-swatches preview-${t}`}
                        aria-hidden
                      >
                        <span className="theme-swatch theme-swatch-1" />
                        <span className="theme-swatch theme-swatch-2" />
                        <span className="theme-swatch theme-swatch-3" />
                      </div>
                      <div className="theme-card-name">
                        {THEME_LABELS[t]}
                      </div>
                      {selected && (
                        <div className="theme-card-check" aria-hidden>
                          ✓
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </Row>

            <Row
              label="Background brightness"
              hint="Scales the whole UI. Useful for matching ambient light."
            >
              <div className="inline-field">
                <input
                  type="range"
                  min={BRIGHTNESS_MIN}
                  max={BRIGHTNESS_MAX}
                  step={BRIGHTNESS_STEP}
                  value={brightness}
                  onChange={(e) => setBrightness(parseFloat(e.target.value))}
                  aria-label="Background brightness"
                />
                <span className="muted" style={{ minWidth: "3.5em" }}>
                  {Math.round(brightness * 100)}%
                </span>
                <Tooltip title="Reset brightness to 100%">
                  <button
                    type="button"
                    onClick={() => setBrightness(DEFAULT_BRIGHTNESS)}
                    disabled={brightness === DEFAULT_BRIGHTNESS}
                  >
                    Reset
                  </button>
                </Tooltip>
              </div>
            </Row>

            <Row label="Font size">
              <div className="segmented">
                {(["small", "medium", "large"] as FontSize[]).map((v) => (
                  <button
                    key={v}
                    type="button"
                    className={
                      settings.appearance.fontSize === v ? "active" : ""
                    }
                    onClick={() => update("appearance", { fontSize: v })}
                  >
                    {v[0]!.toUpperCase() + v.slice(1)}
                  </button>
                ))}
              </div>
            </Row>
          </section>

          {/* ---------- Records ---------- */}
          <section className="settings-section">
            <h3>Records</h3>

            <Row label="Default sort order">
              <div className="segmented">
                {(["newest", "oldest"] as SortOrder[]).map((v) => (
                  <button
                    key={v}
                    type="button"
                    className={
                      settings.records.sortOrder === v ? "active" : ""
                    }
                    onClick={() => update("records", { sortOrder: v })}
                  >
                    {v === "newest" ? "Newest first" : "Oldest first"}
                  </button>
                ))}
              </div>
            </Row>

            <Row label="Group by month">
              <Tooltip title="Group records into month headers in the timeline">
                <label className="switch">
                  <input
                    type="checkbox"
                    checked={settings.records.groupByMonth}
                    onChange={(e) =>
                      update("records", { groupByMonth: e.target.checked })
                    }
                  />
                  <span>
                    {settings.records.groupByMonth ? "On" : "Off"}
                  </span>
                </label>
              </Tooltip>
            </Row>

            <Row
              label="Retention"
              hint="Records older than this are deleted nightly. 'Never' disables cleanup."
            >
              <div className="segmented">
                {(
                  [
                    ["never", "Never"],
                    ["1y", "1 year"],
                    ["3y", "3 years"],
                  ] as [Retention, string][]
                ).map(([v, label]) => (
                  <button
                    key={v}
                    type="button"
                    className={settings.records.retention === v ? "active" : ""}
                    onClick={() => {
                      update("records", { retention: v });
                      void updateServerPref({ retention: v });
                    }}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </Row>
          </section>

          {/* Response style, draft language, model, and usage now live in
              the AI section below so the consumer settings aren't split
              across two panels. See AiSettings.tsx. */}

          {/* ---------- Notifications ---------- */}
          <section className="settings-section">
            <h3>Notifications</h3>

            <Row
              label="Email when a record arrives"
              hint="Sends a notification email each time a forwarded message becomes a record."
            >
              <Tooltip title="Email you whenever a forwarded message becomes a new record">
                <label className="switch">
                  <input
                    type="checkbox"
                    checked={settings.notifications.emailOnForward}
                    onChange={(e) => {
                      update("notifications", {
                        emailOnForward: e.target.checked,
                      });
                      void updateServerPref({
                        notify_on_forward: e.target.checked,
                      });
                    }}
                  />
                  <span>
                    {settings.notifications.emailOnForward ? "On" : "Off"}
                  </span>
                </label>
              </Tooltip>
            </Row>

            <Row
              label="Weekly case summary"
              hint="Sends a Sunday-morning digest of records added over the past 7 days."
            >
              <Tooltip title="Send a weekly Sunday digest of records added in the past 7 days">
                <label className="switch">
                  <input
                    type="checkbox"
                    checked={settings.notifications.weeklySummary}
                    onChange={(e) => {
                      update("notifications", {
                        weeklySummary: e.target.checked,
                      });
                      void updateServerPref({
                        notify_weekly_summary: e.target.checked,
                      });
                    }}
                  />
                  <span>
                    {settings.notifications.weeklySummary ? "On" : "Off"}
                  </span>
                </label>
              </Tooltip>
            </Row>
          </section>

          {/* ---------- Demo ---------- */}
          <section className="settings-section">
            <h3>Demo</h3>

            <Row
              label="Sample case"
              hint="Loads a realistic security-deposit dispute with eight timeline entries (emails, photos, a lease PDF, a phone-call note) so you can see how a finished case looks."
            >
              <div className="inline-field">
                {demoCaseId ? (
                  <>
                    <span className="muted">Demo case loaded</span>
                    <Tooltip title="Permanently remove the seeded demo case and its records">
                      <button
                        type="button"
                        onClick={clearDemo}
                        disabled={demoBusy !== null}
                      >
                        {demoBusy === "clearing"
                          ? "Clearing…"
                          : "Clear demo case"}
                      </button>
                    </Tooltip>
                  </>
                ) : (
                  <Tooltip title="Create a sample case populated with emails, photos, a PDF, and a phone-call note">
                    <button
                      type="button"
                      onClick={loadDemoCase}
                      disabled={demoBusy !== null}
                    >
                      {demoBusy === "loading"
                        ? "Loading…"
                        : "Load demo case"}
                    </button>
                  </Tooltip>
                )}
              </div>
            </Row>

            {demoError && <div className="form-error">{demoError}</div>}
          </section>

          {/* ---------- Export ---------- */}
          <section className="settings-section">
            <h3>Export</h3>

            <Row
              label="Timeline as CSV"
              hint="One row per record across every case — dates, type, from/to, subject, and body text."
            >
              <Tooltip title="Download every record across every case as a CSV file">
                <button
                  type="button"
                  onClick={exportTimelineCsv}
                  disabled={exportBusy !== null}
                >
                  {exportBusy === "csv" ? "Building…" : "Download CSV"}
                </button>
              </Tooltip>
            </Row>

            <Row
              label="Full case as PDF dossier"
              hint="Chronological PDF with every record's headers, body, and attachment references."
            >
              <div className="inline-field">
                <select
                  value={pdfCaseId}
                  onChange={(e) => setPdfCaseId(e.target.value)}
                  disabled={cases.length === 0 || exportBusy !== null}
                >
                  {cases.length === 0 && <option>No cases</option>}
                  {cases.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
                <Tooltip title="Build a chronological PDF dossier of the selected case">
                  <button
                    type="button"
                    onClick={exportCasePdf}
                    disabled={cases.length === 0 || exportBusy !== null}
                  >
                    {exportBusy === "pdf" ? "Building…" : "Download PDF"}
                  </button>
                </Tooltip>
              </div>
            </Row>

            <Row
              label="Full case as ZIP"
              hint="records.json + records.csv + every attachment file, bundled."
            >
              <div className="inline-field">
                <select
                  value={zipCaseId}
                  onChange={(e) => setZipCaseId(e.target.value)}
                  disabled={cases.length === 0 || exportBusy !== null}
                >
                  {cases.length === 0 && <option>No cases</option>}
                  {cases.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
                <Tooltip title="Download the case as a ZIP bundle with records and attachments">
                  <button
                    type="button"
                    onClick={exportCaseZip}
                    disabled={cases.length === 0 || exportBusy !== null}
                  >
                    {exportBusy === "zip" ? "Building…" : "Download ZIP"}
                  </button>
                </Tooltip>
              </div>
            </Row>

            {exportError && <div className="form-error">{exportError}</div>}
          </section>

          {/* ---------- AI ---------- */}
          <AiSettings />

          {/* ---------- Security ---------- */}
          <section className="settings-section">
            <h3>Security</h3>

            <Row
              label="Two-factor authentication"
              hint="Adds a second factor (TOTP or passkey) on top of your account login."
            >
              <div className="inline-field">
                <span className="muted">Not configured</span>
                <Tooltip title="Set up two-factor authentication for your account">
                  <button
                    type="button"
                    onClick={() =>
                      window.alert(
                        "Two-factor authentication will be available in a future update.",
                      )
                    }
                  >
                    Set up
                  </button>
                </Tooltip>
              </div>
            </Row>

            <Row
              label="Show onboarding tour again"
              hint="Replay the three-step introduction that appears for first-time users."
            >
              <Tooltip title="Replay the first-time onboarding tour">
                <button
                  type="button"
                  onClick={() => {
                    try {
                      window.localStorage.removeItem(
                        "casethread:onboarding-complete",
                      );
                    } catch {}
                    window.dispatchEvent(
                      new CustomEvent("casethread:open-onboarding"),
                    );
                    onClose();
                  }}
                >
                  Replay tour
                </button>
              </Tooltip>
            </Row>
          </section>

          {/* ---------- Plan & Billing ---------- */}
          <SubscriptionSettings
            plan={plan}
            daysRemaining={daysRemaining}
            hasSubscription={hasSubscription}
            cancelAtPeriodEnd={cancelAtPeriodEnd}
            currentPeriodEnd={currentPeriodEnd}
            aiMeter={aiMeter}
            onBillingChanged={onBillingChanged}
          />

          {/* ---------- Account ---------- */}
          <section className="settings-section">
            <h3>Account</h3>

            <Row label="Display name">
              <div className="inline-field">
                <input
                  type="text"
                  value={nameDraft}
                  onChange={(e) => setNameDraft(e.target.value)}
                  placeholder={userEmail ?? ""}
                  maxLength={200}
                />
                <Tooltip title="Save the display name shown on letters and exports">
                  <button
                    type="button"
                    onClick={saveDisplayName}
                    disabled={
                      nameSaving || nameDraft === settings.account.displayName
                    }
                  >
                    {nameSaving ? "Saving…" : nameSaved ? "Saved" : "Save"}
                  </button>
                </Tooltip>
              </div>
            </Row>

            <Row label="Signed in as">
              <code className="mono">{userEmail ?? "—"}</code>
            </Row>

            <Row label="Forwarding address on file">
              <code className="mono">{forwardingAddress ?? "—"}</code>
            </Row>

            <Row
              label="Danger zone"
              hint="Permanently deletes every case, record, file and transcript tied to this account. No undo."
            >
              <Tooltip title="Permanently delete your account and every case, record, and file">
                <button
                  type="button"
                  className="btn-danger"
                  onClick={deleteAccount}
                  disabled={deleting}
                >
                  {deleting
                    ? "Deleting…"
                    : deleteConfirming
                      ? "Click again to confirm"
                      : "Delete account and all data"}
                </button>
              </Tooltip>
            </Row>
          </section>
        </div>
      </div>
    </ModalBackdrop>
  );
}

function Row({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="settings-row">
      <div className="settings-row-label">
        <div>{label}</div>
        {hint && <div className="settings-row-hint">{hint}</div>}
      </div>
      <div className="settings-row-control">{children}</div>
    </div>
  );
}

// --- helpers ---------------------------------------------------------------

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

function slugify(s: string): string {
  return (
    s
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 40) || "case"
  );
}

function csvEscape(v: unknown): string {
  if (v === null || v === undefined) return "";
  const s = String(v);
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function toCsv(
  rows: Array<{ caseName: string; record: CaseRecord }>,
): string {
  const headers = [
    "Case",
    "Date",
    "Added",
    "Direction",
    "Source type",
    "From",
    "To",
    "Via",
    "Subject",
    "Body",
    "File name",
    "File type",
  ];
  const lines: string[] = [headers.map(csvEscape).join(",")];
  for (const { caseName, record: r } of rows) {
    lines.push(
      [
        caseName,
        r.record_date,
        r.logged_at,
        r.direction,
        r.source_type,
        r.from_addr,
        r.to_addr,
        r.forwarded_via,
        r.subject,
        r.body_text,
        r.file_name,
        r.file_mime_type,
      ]
        .map(csvEscape)
        .join(","),
    );
  }
  return lines.join("\r\n");
}

function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// Keep applyAppearance in the static import list so tree-shaking doesn't drop
// it; App.tsx calls it once on boot.
void applyAppearance;
